import * as z from 'zod';
import type { ZodOpenApiPathsObject } from 'zod-openapi';
import { problemResponse } from './common.js';
import { defineEvent, type EventDefinition } from './events.js';
import { N8nTestResultSchema } from './settings.js';

/**
 * Every event DeliCRM sends to n8n (spec §7.5). Producers may only emit definitions listed here,
 * and docs/events.md is generated from this list (`pnpm events:docs`), so n8n always has an
 * accurate catalogue. Add new events at the end; change an existing one only by a new version.
 */
export const SystemTestEvent = defineEvent({
  type: 'system.test',
  version: 1,
  description:
    'Sent when an admin presses "send test event" in the settings. Use it to check that the n8n workflow receives and verifies DeliCRM events.',
  data: z.object({
    message: z.string(),
    requestedBy: z.string().meta({ description: 'Name of the admin who sent it' }),
  }),
});

export const PricesChangedEvent = defineEvent({
  type: 'prices.changed',
  version: 1,
  description:
    "Sent when something that changes selling prices is saved: a base price or a product going on / off sale, a partner or personal discount, or a promotion. The store refreshes its cached price lists (all of them, or only this customer's when customerId is set).",
  data: z.object({
    cause: z
      .enum(['base_price', 'personal_discount', 'promotion'])
      .meta({ description: 'What changed' }),
    customerId: z
      .uuid()
      .nullable()
      .meta({ description: "Set when only this customer's prices changed (a personal discount)" }),
  }),
});

const orderSummary = {
  orderId: z.uuid(),
  number: z.number().int().meta({ description: 'Order number people use' }),
  channel: z.enum(['woo', 'manager', 'legacy']),
  customer: z.object({ id: z.uuid(), name: z.string(), type: z.enum(['b2c', 'b2b']) }),
  status: z.string(),
  paymentStatus: z.string(),
  paymentMethod: z.string().nullable(),
  total: z.number().int().meta({ description: 'Kopecks' }),
  wooOrderId: z
    .number()
    .int()
    .nullable()
    .meta({ description: 'The WooCommerce order id for store orders, otherwise null' }),
};

export const OrderCreatedEvent = defineEvent({
  type: 'order.created',
  version: 1,
  description:
    'A new order was placed (manager orders when they leave Draft; store orders when they arrive). Drafts are not announced.',
  data: z.object(orderSummary),
});

export const OrderStatusChangedEvent = defineEvent({
  type: 'order.status_changed',
  version: 1,
  description:
    'Every order status transition (spec §6): placed, paid → confirmed, shipped, delivered, completed, cancelled, returned.',
  data: z.object({ ...orderSummary, from: z.string().meta({ description: 'Previous status' }) }),
});

export const OrderPaymentStatusChangedEvent = defineEvent({
  type: 'order.payment_status_changed',
  version: 1,
  description:
    "The order's payment status changed: unpaid, partially_paid, paid, overpaid or refunded.",
  data: z.object({
    ...orderSummary,
    from: z.string().meta({ description: 'Previous payment status' }),
  }),
});

export const PaymentRecordedEvent = defineEvent({
  type: 'payment.recorded',
  version: 1,
  description:
    'Money came in or was refunded (any provider, including payments typed in by staff).',
  data: z.object({
    paymentId: z.uuid(),
    orderId: z.uuid(),
    direction: z.enum(['in', 'refund']),
    amount: z.number().int().meta({ description: 'Kopecks' }),
    method: z.string(),
    provider: z.string(),
  }),
});

export const StockChangedEvent = defineEvent({
  type: 'stock.changed',
  version: 1,
  description:
    "A product's stock moved: a stock count, an import, or an order reserving, releasing, shipping or restocking it. The store sets the product's quantity to `available`.",
  data: z.object({
    productId: z.uuid(),
    sku: z.string(),
    onHand: z.number().int().meta({ description: 'On the shelf' }),
    reserved: z.number().int().meta({ description: 'Held by placed orders not yet shipped' }),
    available: z.number().int().meta({ description: 'onHand − reserved: what can still be sold' }),
  }),
});

const pointsBalances = {
  customerId: z.uuid(),
  spendable: z.number().int().meta({ description: 'Points the customer can spend now' }),
};

export const PointsAccruedEvent = defineEvent({
  type: 'points.accrued',
  version: 1,
  description:
    'A retail order was paid and earned Q-points. They are pending (visible, not yet spendable) until the order completes after its return window.',
  data: z.object({
    ...pointsBalances,
    orderId: z.uuid(),
    points: z.number().int(),
    pending: z.number().int().meta({ description: 'All pending points of the customer' }),
  }),
});

export const PointsActivatedEvent = defineEvent({
  type: 'points.activated',
  version: 1,
  description:
    "An order completed: the Q-points it earned became spendable. `expiresAt` is fixed now and never changes (null = they don't expire).",
  data: z.object({
    ...pointsBalances,
    orderId: z.uuid(),
    points: z.number().int(),
    expiresAt: z.iso.datetime().nullable(),
  }),
});

export const PointsExpiringEvent = defineEvent({
  type: 'points.expiring',
  version: 1,
  description:
    'Q-points of a customer fall due within 14 days (sent once per batch). Use it for a reminder message.',
  data: z.object({
    ...pointsBalances,
    points: z.number().int().meta({ description: 'Points due on expiresAt' }),
    expiresAt: z.iso.datetime(),
  }),
});

export const PointsExpiredEvent = defineEvent({
  type: 'points.expired',
  version: 1,
  description: 'Spendable Q-points passed their due date and expired.',
  data: z.object({ ...pointsBalances, points: z.number().int() }),
});

export const PointsChangedEvent = defineEvent({
  type: 'points.changed',
  version: 1,
  description:
    "Any other change of a customer's Q-points: spent on an order (redeem), given back or taken back (reverse), a manual correction (adjust), or a point value change (convert).",
  data: z.object({
    ...pointsBalances,
    kind: z.enum(['redeem', 'reverse', 'adjust', 'convert']),
    points: z.number().int().meta({ description: 'Signed change' }),
    orderId: z.uuid().nullable(),
    pending: z.number().int(),
  }),
});

export const EVENT_CATALOGUE: readonly EventDefinition[] = [
  SystemTestEvent,
  PricesChangedEvent,
  OrderCreatedEvent,
  OrderStatusChangedEvent,
  OrderPaymentStatusChangedEvent,
  PaymentRecordedEvent,
  StockChangedEvent,
  PointsAccruedEvent,
  PointsActivatedEvent,
  PointsExpiringEvent,
  PointsExpiredEvent,
  PointsChangedEvent,
];

/**
 * Events that also go to the store webhook (when one is set): price and stock changes, and status
 * changes of orders that came from the store. Everything goes to n8n regardless.
 */
export function wantedByStore(envelope: { type: string; data: unknown }): boolean {
  if (envelope.type === PricesChangedEvent.type || envelope.type === StockChangedEvent.type) {
    return true;
  }
  if (envelope.type !== OrderStatusChangedEvent.type) return false;
  const data = envelope.data as { wooOrderId?: unknown };
  return typeof data.wooOrderId === 'number';
}

/** Signature headers on every delivery (see docs/events.md for verification in n8n). */
export const EVENT_HEADERS = {
  id: 'x-deli-event-id',
  type: 'x-deli-event-type',
  timestamp: 'x-deli-timestamp',
  signature: 'x-deli-signature',
} as const;

export const OutboxStatusSchema = z
  .object({
    webhookConfigured: z.boolean(),
    secretConfigured: z.boolean(),
    pending: z.number().int(),
    failed: z.number().int(),
    lastSentAt: z.iso.datetime().nullable(),
  })
  .meta({ id: 'OutboxStatus' });

export type OutboxStatusDto = z.infer<typeof OutboxStatusSchema>;

const json = <T extends z.ZodType>(schema: T) => ({ 'application/json': { schema } });
const guarded = {
  '401': problemResponse('Not signed in'),
  '403': problemResponse('Missing permission'),
};

export const integrationPaths: ZodOpenApiPathsObject = {
  '/api/v1/integrations/n8n/status': {
    get: {
      summary: 'n8n delivery status: configuration and outbox counts',
      responses: { '200': { description: 'OK', content: json(OutboxStatusSchema) }, ...guarded },
    },
  },
  '/api/v1/integrations/store/status': {
    get: {
      summary: 'Store webhook (DeliStore plugin) delivery status: configuration and outbox counts',
      responses: { '200': { description: 'OK', content: json(OutboxStatusSchema) }, ...guarded },
    },
  },
  '/api/v1/integrations/n8n/test': {
    post: {
      summary: 'Send a signed system.test event to the configured n8n webhook right now',
      responses: {
        '200': { description: 'Attempted', content: json(N8nTestResultSchema) },
        ...guarded,
      },
    },
  },
  '/api/v1/integrations/inbox/{id}/replay': {
    post: {
      summary: 'Put an inbound event back in the processing queue',
      requestParams: { path: z.object({ id: z.uuid() }) },
      responses: {
        '204': { description: 'Queued again' },
        '404': problemResponse('No such event'),
        ...guarded,
      },
    },
  },
  '/api/v1/integrations/n8n/outbox/discard': {
    post: {
      summary:
        'Discard events still waiting for delivery (e.g. a backlog from before n8n was set up)',
      responses: {
        '200': {
          description: 'Discarded',
          content: json(z.object({ discarded: z.number().int() })),
        },
        ...guarded,
      },
    },
  },
};
